/**
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2016, 2021. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 */
/*global module require*/
/*eslint-env browser*/
"use strict";

var EventEmitter = require("events"),
  Log = require("../../../lib/client/setup/log"),
  nodeUtil = require("util");
module.exports = ValidationTrackerWrapper;
// ValidationTrackerWrapper wraps ValidationTracker
// to avoid bug in Chrome React extension Components tab
// components with this.props.something as EventEmitter
// appear stuck in "Loading" instead of showing props and state,
// which was making it difficult to debug issues.
function ValidationTrackerWrapper(target) {
  var _this = this;
  this.delegate = new ValidationTracker(target);
  this.authDone = this.delegate.authDone;
  this.authMap = this.delegate.authMap;
  this.validators = this.delegate.validators.bind(this.delegate);
  this.allPending = this.delegate.allPending.bind(this.delegate);
  this.pending = this.delegate.pending.bind(this.delegate);

  // list of function names from:
  // https://nodejs.org/dist/v11.13.0/docs/api/events.html
  var delegatePublicFns = ['addListener', 'emit', 'eventNames', 'getMaxListeners', 'listenerCount', 'listeners', 'off', 'on', 'once', 'prependListener', 'prependOnceListener', 'removeAllListeners', 'removeListener', 'setMaxListeners', 'rawListeners'];
  delegatePublicFns.forEach(function (fnName) {
    // e.g. this.on = this.delegate.on.bind(this.delegate);
    _this[fnName] = _this.delegate[fnName].bind(_this.delegate);
  });
}

// Events have shape: { type: start|end, source: ValidatorController }
var targetEventTypes = ["load", "unload", "authenticate", "populate", "validate"];
var logger = Log.getLogger("validation");

// ValidationTracker observes the validators on the page in the aggregate. It provides methods to
// identify validators that have unfinished work, and emits high-level events when all work has
// been finished.
//
// ValidationTracker monitors validators through a shared EventEmitter `target` that all validators
// must emit events from.
//
// Events:
// - "{foo}Settled": emitted when validators have responded to all pending events of type "foo".
// - "allSettled": emitted when validators have responded to all pending events of *all* types.
//                 This means the model is in a steady state, awaiting user action.
//
// @param {EventEmitter} target The shared emitter used by all validators
function ValidationTracker(target) {
  // Map of ValidatorController.id -> ValidatorController
  this._validators = Object.create(null);

  // Stores a count of events that validators have not completed yet. Keyed by eventType, then validator id.
  // map<eventType -> map<validatorId -> count>>
  this._pending = Object.create(null);
  // authMap keeps track of authenticate calls that have been sent; authDone keeps track of authenticate calls completed
  // both are needed in order to ensure that only one auth call is made to a service broker (even if there are multiple instances
  // of a service on the form)
  this.authMap = Object.create(null);
  this.authDone = Object.create(null);
  var self = this;
  targetEventTypes.forEach(function (type) {
    self._pending[type] = Object.create(null);
    target.on(type, self._onEvent.bind(self, type));
  });
  EventEmitter.call(this);
}
nodeUtil.inherits(ValidationTracker, EventEmitter);
ValidationTracker.prototype.eventTypes = ["allSettled"].concat(targetEventTypes.map(function (type) {
  return type + "Settled";
}));
ValidationTracker.prototype._onEvent = function (eventType, data) {
  var _this2 = this;
  var source = data.source,
    sourceId = source.id;
  var pendingSet = this._pending[eventType];
  if (eventType === "unload") {
    // ValidatorController instance is being booted out of the page. Delete its entry, and
    // any pending events it has, since they will never complete.
    if (this._validators[sourceId]) {
      logger.debug("Validator evicted:", sourceId);
    }
    targetEventTypes.forEach(function (type) {
      var pendingSet = _this2._pending[type],
        count = pendingSet[sourceId];
      if (typeof count === 'number') {
        logger.debug("Abort " + count + " pending '" + type + "' event(s) for " + sourceId);
      }
      delete pendingSet[sourceId];
    });
    delete this._validators[sourceId];
    this._emit("validatorEvicted");
    return;
  }

  // Not an unload event, therefore it must be some eventType that has start..end
  if (data.type === "start") {
    var count = pendingSet[sourceId];
    if (typeof count !== "number") {
      count = 0;
    }
    pendingSet[sourceId] = count + 1;
    //logger.debug("Send '" + eventType + "' --> ", sourceId);
  } else if (data.type === "end") {
    //logger.debug("Receive '" + eventType + "' <--", sourceId);
    if (typeof pendingSet[sourceId] === "number") {
      pendingSet[sourceId]--;
    } else {
      // should not happen - got an end event with no start?
      logger.warn("Received '" + eventType + " <-- ", sourceId, "out of order!");
    }
    if (pendingSet[sourceId] === 0) {
      delete pendingSet[sourceId];
      if (Object.keys(pendingSet).length === 0) {
        // No more events of this type are pending for any validator, dispatch a settled event
        this._emit(eventType + "Settled");
        if (this.allPending().length === 0) {
          // No more events of *any* type are pending for any validator, dispatch allSettled
          this._emit("allSettled");
        }
      }
    }
    if (eventType === "load") {
      this._validators[sourceId] = source;
    }
  }
};
ValidationTracker.prototype._emit = function (type, data) {
  logger.debug("Dispatching", type);
  this.emit(type, data);
};
// Returns validators who have not yet responded to an outstanding event of the given type.
// e.g. pending("load") - returns validators that have not finished loading
ValidationTracker.prototype.pending = function (type) {
  var _this3 = this;
  var keys = Object.keys(this._pending[type] || {});
  if (type === "load") {
    // load is an exception, as we have no proper entry in _validators yet
    return keys.map(function (id) {
      return {
        src: id
      };
    });
  }
  return keys.map(function (id) {
    return _this3._validators[id];
  });
};
// Returns validators who have not yet responding to an outstanding event of any type.
ValidationTracker.prototype.allPending = function () {
  var _this4 = this;
  var idsMap = {};
  targetEventTypes.forEach(function (type) {
    Object.keys(_this4._pending[type] || {}).forEach(function (id) {
      return idsMap[id] = 1;
    });
  });
  return Object.keys(idsMap).map(function (id) {
    return _this4._validators[id];
  });
};
// Returns all validators.
ValidationTracker.prototype.validators = function () {
  var vals = this._validators,
    result = [];
  Object.keys(vals).forEach(function (key) {
    result.push(vals[key]);
  });
  return result;
};